import { useState } from 'react';
import {
  ActionButton,
  Alert,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  PeriodCell,
  RowAction,
  RowActions,
  Stack,
  TagChip,
  TextField,
  titleCellSx,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { InviteLanguageField, type InviteLanguage } from './InviteLanguageField';
import { GOVERNMENT_MEMBERSHIP_ROLES } from '@munifocus/access';
import {
  useAddGovernmentMember,
  useGovernmentMembers,
  useRemoveGovernmentMember,
} from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import { formatDate } from '../../lib/relative-time';

/**
 * Council, counsel, clerks, finance and staff at this municipality.
 *
 * These used to be free-text names on the registry row — `clerk_name`,
 * `signing_officers` — good enough to print on letterhead and useless as
 * accounts. They are people who sign in now.
 */
export function MembersPanel() {
  const { t, i18n } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const toast = useMutationToast();
  const { active, can } = useScope();
  const confirm = useConfirmAction();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : null;

  const members = useGovernmentMembers(localGovernmentId);
  const add = useAddGovernmentMember(localGovernmentId ?? '');
  const remove = useRemoveGovernmentMember(localGovernmentId ?? '');

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<string>('clerk');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState<InviteLanguage>('en');

  const rows = members.data ?? [];

  /**
   * The clerk seat is the one that carries council's powers — binding a
   * contract, appointing an officer — because council itself holds no accounts.
   * Granting it is therefore a bigger act than granting any other seat, and the
   * portal says so rather than leaving it to be discovered.
   */
  const grantsBindingPowers = role === 'clerk';

  /**
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not. Closing and clearing after a failed
   * save threw the officer's input away behind an error toast.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  if (!localGovernmentId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.members.pickMunicipality')}
        </Typography>
      </HairlineCard>
    );
  }

  return (
    <Stack spacing={2}>
      <DataTable
        rows={rows}
        getRowKey={(row) => row.id}
        isLoading={members.isLoading}
        emptyLabel={t('gov.admin.members.empty')}
        pageSize={25}
        initialSortKey="name"
        search={{
          placeholder: t('gov.admin.members.searchPlaceholder'),
          of: (row) => `${row.userName} ${row.userEmail} ${row.title}`,
        }}
        action={
          can('member:appoint') ? (
            <ActionButton
              label={t('gov.admin.members.add')}
              icon={Icons.Plus}
              onClick={() => setAdding(true)}
            />
          ) : null
        }
        columns={[
          {
            key: 'name',
            label: t('gov.admin.members.name'),
            primary: true,
            sortValue: (row) => row.userName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={titleCellSx}>
                  {row.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.userEmail}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'role',
            label: t('gov.admin.members.role'),
            sortValue: (row) => row.role,
            cell: (row) => <TagChip label={t(`gov.admin.roles.${row.role}`)} />,
          },
          {
            key: 'access',
            label: t('gov.admin.members.access'),
            cell: (row) => (
              <Typography variant="body2" color="text.secondary">
                {/* Without file:read here the municipality is on the limited
                    licence, and every seat sees files only as the public does. */}
                {t(
                  can('file:read')
                    ? `gov.admin.roleAccess.${row.role}`
                    : `gov.admin.roleAccessLimited.${row.role}`,
                )}
              </Typography>
            ),
          },
          {
            key: 'title',
            label: t('gov.admin.members.title'),
            sortValue: (row) => row.title,
            cell: (row) => row.title || '—',
          },
          {
            key: 'term',
            label: t('gov.admin.members.term'),
            nowrap: true,
            sortValue: (row) => row.appointedOn,
            cell: (row) => (
              <PeriodCell
                start={row.appointedOn ? formatDate(row.appointedOn, i18n.language) : null}
                end={row.expiresOn ? formatDate(row.expiresOn, i18n.language) : null}
                noStartLabel={t('gov.admin.members.noStart')}
                noEndLabel={t('gov.admin.members.openEnded')}
              />
            ),
          },
          ...(can('member:revoke')
            ? [
                {
                  ...actionsColumn,
                  cell: (row: (typeof rows)[number]) => (
                    <RowActions>
                      <RowAction
                        label={t('gov.admin.members.remove')}
                        icon={Icons.Trash2}
                        tone="danger"
                        onClick={async () => {
                          // Ending a seat ends the authority that came with it —
                          // a clerk removed here loses every power council's
                          // decisions flow through.
                          if (
                            !(await confirm({
                              title: t('gov.admin.members.removeTitle'),
                              body: t('gov.admin.members.removeBody', {
                                name: row.userName,
                                role: t(`gov.admin.roles.${row.role}`),
                              }),
                              confirmLabel: t('gov.admin.members.remove'),
                              tone: 'danger',
                            }))
                          ) {
                            return;
                          }
                          await run('gov.admin.members.removed', () => remove.mutateAsync(row.id));
                        }}
                      />
                    </RowActions>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.members.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('gov.admin.members.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              type="email"
              helperText={t('gov.admin.members.emailHelp')}
            />
            <TextField
              label={t('gov.admin.members.name')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label={t('gov.admin.members.role')}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              {GOVERNMENT_MEMBERSHIP_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.admin.roles.${r}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.admin.members.title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              helperText={t('gov.admin.members.titleHelp')}
            />
            <InviteLanguageField value={language} onChange={setLanguage} />
            {grantsBindingPowers && (
              <Alert severity="info">{t('gov.admin.members.clerkBindingPowers')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!email || !displayName || add.isPending}
            onClick={async () => {
              const ok = await run('gov.admin.members.added', () =>
                add.mutateAsync({
                  email,
                  displayName,
                  localGovernmentId,
                  role: role as never,
                  title,
                  language,
                }),
              );
              if (!ok) return;
              setAdding(false);
              setEmail('');
              setDisplayName('');
              setTitle('');
              setLanguage('en');
            }}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
